import { reduxUtil } from '@cmg/common';
import { SnackbarManager } from '@cmg/design-system';
import { combineReducers } from 'redux';
import type { SagaIterator } from 'redux-saga';
import { call, put, takeLatest, takeLeading } from 'redux-saga/effects';

import type {
  ConsentFailureBody,
  FetchConsentInfoResponse,
  SubmitConsentResponse,
} from '../../../common/api/identityApiClient';
import identityApiClient from '../../../common/api/identityApiClient';
import type { RootState } from '../../../common/redux/rootReducer';
import topLevelNavigation from '../../../common/util/topLevelNavigation';
import type { ConsentInfo } from '../../../types/domain/consent/consentInfo';
import { ConsentResponseCode } from '../../../types/domain/consent/consentInfo';

/**
 * ACTION TYPES
 */
enum ActionTypes {
  FETCH_CONSENT_INFO_REQUEST = 'identity/FETCH_CONSENT_INFO_REQUEST',
  FETCH_CONSENT_INFO_SUCCESS = 'identity/FETCH_CONSENT_INFO_SUCCESS',
  FETCH_CONSENT_INFO_EXPIRED = 'identity/FETCH_CONSENT_INFO_EXPIRED',
  FETCH_CONSENT_INFO_FAILURE = 'identity/FETCH_CONSENT_INFO_FAILURE',
  SUBMIT_CONSENT_REQUEST = 'identity/SUBMIT_CONSENT_REQUEST',
  SUBMIT_CONSENT_EXPIRED = 'identity/SUBMIT_CONSENT_EXPIRED',
  SUBMIT_CONSENT_REFUSED = 'identity/SUBMIT_CONSENT_REFUSED',
  SUBMIT_CONSENT_FAILURE = 'identity/SUBMIT_CONSENT_FAILURE',
}

/**
 * ACTION CREATORS
 */
export const fetchConsentInfo = (params: { returnUrl: string }) => ({
  type: ActionTypes.FETCH_CONSENT_INFO_REQUEST,
  payload: {
    returnUrl: params.returnUrl,
  },
});

export const fetchConsentInfoSuccess = (params: { consentInfo: ConsentInfo }) => ({
  type: ActionTypes.FETCH_CONSENT_INFO_SUCCESS,
  payload: {
    consentInfo: params.consentInfo,
  },
});

/** The authorize request behind `returnUrl` is no longer valid; nothing was granted. */
export const fetchConsentInfoExpired = () => ({
  type: ActionTypes.FETCH_CONSENT_INFO_EXPIRED,
});

export const fetchConsentInfoFailure = () => ({
  type: ActionTypes.FETCH_CONSENT_INFO_FAILURE,
});

export const submitConsent = (params: {
  returnUrl: string;
  granted: boolean;
  rememberConsent: boolean;
}) => ({
  type: ActionTypes.SUBMIT_CONSENT_REQUEST,
  payload: {
    returnUrl: params.returnUrl,
    granted: params.granted,
    rememberConsent: params.rememberConsent,
  },
});

/** The request expired between load and decision. Same screen as an expired load. */
export const submitConsentExpired = () => ({
  type: ActionTypes.SUBMIT_CONSENT_EXPIRED,
});

/**
 * The decision was recorded, but the `returnUrl` the API answered with was refused by the
 * navigation guard, so no navigation happened.
 */
export const submitConsentRefused = () => ({
  type: ActionTypes.SUBMIT_CONSENT_REFUSED,
});

export const submitConsentFailure = () => ({
  type: ActionTypes.SUBMIT_CONSENT_FAILURE,
});

type Actions = {
  [ActionTypes.FETCH_CONSENT_INFO_REQUEST]: ReturnType<typeof fetchConsentInfo>;
  [ActionTypes.FETCH_CONSENT_INFO_SUCCESS]: ReturnType<typeof fetchConsentInfoSuccess>;
  [ActionTypes.FETCH_CONSENT_INFO_EXPIRED]: ReturnType<typeof fetchConsentInfoExpired>;
  [ActionTypes.FETCH_CONSENT_INFO_FAILURE]: ReturnType<typeof fetchConsentInfoFailure>;
  [ActionTypes.SUBMIT_CONSENT_REQUEST]: ReturnType<typeof submitConsent>;
  [ActionTypes.SUBMIT_CONSENT_EXPIRED]: ReturnType<typeof submitConsentExpired>;
  [ActionTypes.SUBMIT_CONSENT_REFUSED]: ReturnType<typeof submitConsentRefused>;
  [ActionTypes.SUBMIT_CONSENT_FAILURE]: ReturnType<typeof submitConsentFailure>;
};

/**
 * REDUCERS
 */
const { createReducer } = reduxUtil;

/**
 * `expired` is for `INVALID_REQUEST` only; anything else is our fault, not the request's.
 * `failed` sends the user to the error route, so it must not absorb a request the user could
 * legitimately restart from the app that sent them.
 */
export type ConsentStatus = 'loading' | 'ready' | 'expired' | 'failed';

/** The decision in flight, if any. Both buttons lock so a second click cannot burn the code. */
export type PendingDecision = 'grant' | 'deny';

export type ReducerState = {
  status: ConsentStatus;
  consentInfo: ConsentInfo | null;
  pendingDecision: PendingDecision | null;
};

export const initialState: ReducerState = {
  status: 'loading',
  consentInfo: null,
  pendingDecision: null,
};

const statusReducer = createReducer<ReducerState['status'], Actions>(initialState.status, {
  [ActionTypes.FETCH_CONSENT_INFO_SUCCESS]: () => 'ready',
  [ActionTypes.FETCH_CONSENT_INFO_EXPIRED]: () => 'expired',
  [ActionTypes.FETCH_CONSENT_INFO_FAILURE]: () => 'failed',
  [ActionTypes.SUBMIT_CONSENT_EXPIRED]: () => 'expired',
});

const consentInfoReducer = createReducer<ReducerState['consentInfo'], Actions>(
  initialState.consentInfo,
  {
    [ActionTypes.FETCH_CONSENT_INFO_SUCCESS]: (curState, { payload }) => payload.consentInfo,
  }
);

/**
 * Locked on request, and unlocked only by an outcome the user can act on again.
 *
 * There is deliberately no entry for a decision that navigated: the page is on its way out, and a
 * second decision sent behind an in-flight navigation would race the one the user actually made.
 * The screen stays locked until the browser leaves.
 *
 * The first decision also wins here, not just in the saga. `takeLeading` drops the second request,
 * but the reducer sees it either way — so without the `??` the screen would report "Cancelling…" for
 * a grant that is already in flight and about to navigate.
 */
const pendingDecisionReducer = createReducer<ReducerState['pendingDecision'], Actions>(
  initialState.pendingDecision,
  {
    [ActionTypes.SUBMIT_CONSENT_REQUEST]: (curState, { payload }) =>
      curState ?? (payload.granted ? 'grant' : 'deny'),
    [ActionTypes.SUBMIT_CONSENT_EXPIRED]: () => null,
    [ActionTypes.SUBMIT_CONSENT_REFUSED]: () => null,
    [ActionTypes.SUBMIT_CONSENT_FAILURE]: () => null,
  }
);

export default combineReducers<ReducerState>({
  status: statusReducer,
  consentInfo: consentInfoReducer,
  pendingDecision: pendingDecisionReducer,
});

/**
 * SELECTORS
 */
const selectState = (state: RootState): ReducerState => state.consent;
export const selectStatus = (state: RootState) => selectState(state).status;
export const selectConsentInfo = (state: RootState) => selectState(state).consentInfo;
export const selectPendingDecision = (state: RootState) => selectState(state).pendingDecision;

/**
 * SAGAS
 */

/**
 * A failure body is the API's own shape only when the request reached the API — a transport failure
 * substitutes a generic one — so the code is checked rather than assumed.
 */
const isInvalidRequest = (data: unknown) =>
  !!data &&
  typeof data === 'object' &&
  (data as ConsentFailureBody).code === ConsentResponseCode.INVALID_REQUEST;

/**
 * A 2xx is not by itself a `ConsentInfo`. The consent endpoints sit behind the session cookie, and
 * the cookie handler's default challenge is a 302 to a login page, which axios follows inside the
 * XHR — so a 200 carrying something else is reachable without anything being wrong on the server.
 * The screen indexes `scopes` unguarded; failing the check here shows the error page instead.
 *
 * Every field the screen states as fact is checked, not only the ones that would throw. A payload
 * missing `redirectUri` or `userDisplayName` renders an authorization prompt whose redirect target
 * is blank and whose button reads "Authorize as undefined" — the two facts the decision rests on,
 * both absent, and nothing on screen saying so. `canRemember` is not checked: it only gates a
 * checkbox, and absent reads as off, which is the safe side.
 */
const isConsentInfo = (data: unknown): data is ConsentInfo =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as ConsentInfo).clientName === 'string' &&
  typeof (data as ConsentInfo).redirectUri === 'string' &&
  typeof (data as ConsentInfo).userDisplayName === 'string' &&
  Array.isArray((data as ConsentInfo).scopes);

export function* fetchConsentInfoSaga({
  payload,
}: Actions[ActionTypes.FETCH_CONSENT_INFO_REQUEST]): SagaIterator {
  const response: FetchConsentInfoResponse = yield call(
    identityApiClient.fetchConsentInfo,
    payload
  );

  if (response.ok && isConsentInfo(response.data)) {
    yield put(fetchConsentInfoSuccess({ consentInfo: response.data }));
  } else if (isInvalidRequest(response.data)) {
    yield put(fetchConsentInfoExpired());
  } else {
    yield put(fetchConsentInfoFailure());
  }
}

/**
 * Submits the decision and performs the top-level navigation the API answers with.
 *
 * The API must never answer with a 302 — axios follows redirects inside the XHR, which mints and
 * burns the authorization code where no browser can use it (ticket 11) — so the redirect arrives as
 * JSON and is navigated to from here, through the guard rather than by assigning `location` raw.
 */
export function* submitConsentSaga({
  payload,
}: Actions[ActionTypes.SUBMIT_CONSENT_REQUEST]): SagaIterator {
  const response: SubmitConsentResponse = yield call(identityApiClient.submitConsent, payload);

  if (response.ok) {
    // Deny returns a returnUrl too — Duende turns it into an error=access_denied redirect back to
    // the client, so Cancel is not a dead end.
    const navigated: boolean = yield call(topLevelNavigation.navigate, response.data.returnUrl);
    if (navigated) {
      return;
    }

    // Refused as unsafe. The decision is already recorded server-side, so there is nothing to
    // retry — say so rather than leaving the buttons spinning at a redirect that will never happen.
    yield put(submitConsentRefused());
    SnackbarManager.error(
      'Your decision was submitted, but this app sent an invalid return address.'
    );
    return;
  }

  if (isInvalidRequest(response.data)) {
    yield put(submitConsentExpired());
    return;
  }

  yield put(submitConsentFailure());
  SnackbarManager.error('Something went wrong. Your decision was not submitted.');
}

export function* consentSaga() {
  yield takeLatest<Actions[ActionTypes.FETCH_CONSENT_INFO_REQUEST]>(
    ActionTypes.FETCH_CONSENT_INFO_REQUEST,
    fetchConsentInfoSaga
  );
  // `takeLeading`, not `takeLatest`: a second decision arriving while one is in flight is dropped,
  // not preferred. Escape reaches its listener through a passive effect, so for one tick after a
  // click that listener still sees no decision pending and can fire a deny behind a grant; whichever
  // resolved first would otherwise choose the redirect.
  yield takeLeading<Actions[ActionTypes.SUBMIT_CONSENT_REQUEST]>(
    ActionTypes.SUBMIT_CONSENT_REQUEST,
    submitConsentSaga
  );
}
